import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { FC, Fragment, useMemo, useRef, useState } from "react";
import { AiChatEditedMessageInput, AiChatMessageView } from "generated/graphql";
import theme from "theme";
import { P } from "theme/typography";
import { Edit } from "@mui/icons-material";
import CopyToClipboard from "components/CopyToClipboard";
import RainbowText, { Annotation } from "../../../components/RainbowText";
import { StateHandle } from "StateHandle";
import { type ProfileResponseValue, useTestJsonParser, isMultiResponse, SingleResponseJson } from "./useTestJsonParser";
import { AiChatMessageViewWithIndex } from "./aiChatLoader";

export const ProfileResponseTable: FC<{
  updateMatches: (queryString: string) => void;
  content: string;
}> = ({ content, updateMatches }) => {
  const jsonParser = useTestJsonParser();

  const jsonResponses: SingleResponseJson[] = useMemo(() => {
    const parsed = jsonParser(content);

    if (isMultiResponse(parsed)) return parsed.items;
    else if (parsed !== undefined) return [parsed];
    return [];
  }, [content]);

  const displayValue = (maybeValue: ProfileResponseValue | undefined) => {
    const value =
      maybeValue?.value ||
      maybeValue?.["YYYY-MM-DD_value"] ||
      maybeValue?.closest_value ||
      maybeValue?.closest_values ||
      maybeValue?.correct_value ||
      maybeValue?.integer_value ||
      maybeValue?.float_value;
    if (value === undefined) {
      return "missing";
    } else if (value === null) {
      return "null";
    } else if (typeof value === "string") {
      return `"${value}"`;
    } else if (typeof value === "boolean") {
      return value ? "true" : "false";
    } else if (Array.isArray(value)) {
      return (value as string[]).join(", ");
    } else {
      return value.toString();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}>
      {jsonResponses.map((json, index) => {
        const valueEntries = Object.entries(json).filter(([_key, value]) => typeof value !== "string");
        const noteEntry = json["summary"];
        const cotEntry = json["chain_of_thought"];
        return (
          <Fragment key={index}>
            {jsonResponses.length > 1 && <P>Response {index + 1}</P>}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key*</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {valueEntries.map(([key, value]) => {
                  const castValue = value as ProfileResponseValue | null | undefined;
                  return (
                    <TableRow key={key}>
                      <TableCell>
                        <P variant="xsBold">{key}</P>
                      </TableCell>
                      <TableCell>
                        <P variant="xs">{displayValue(castValue ?? undefined)}</P>
                      </TableCell>
                      <TableCell>
                        {castValue?.raw ? (
                          <P
                            onClick={() => {
                              updateMatches(castValue.raw ?? "");
                            }}
                            variant="xs"
                            sx={{
                              ...theme.mixins.linkLike,
                            }}>
                            {castValue?.raw}
                          </P>
                        ) : (
                          <P
                            variant="xs"
                            sx={{
                              color: "text.secondary",
                            }}>
                            No reference
                          </P>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {typeof noteEntry === "string" && (
                  <TableRow>
                    <TableCell>
                      <P variant="xsBold">Notes</P>
                    </TableCell>
                    <TableCell>
                      <P variant="xs">{noteEntry}</P>
                    </TableCell>
                  </TableRow>
                )}
                {typeof cotEntry === "string" && (
                  <TableRow>
                    <TableCell>
                      <P variant="xsBold">CoT</P>
                    </TableCell>
                    <TableCell>
                      <P variant="xs">{cotEntry}</P>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Fragment>
        );
      })}
    </Box>
  );
};

function getTextContentForMessage(message: AiChatMessageView): string {
  return message.content ?? (message.contentParts ?? []).find((part) => part.contentType === "text")?.textContent ?? "";
}

export const AiChatMessageComponent: FC<{
  message: AiChatMessageViewWithIndex;
  initialExpand?: boolean;
  updateMessage?: (message: AiChatEditedMessageInput) => void;
  editable?: boolean;
  updateMatches: (queryString: string) => void;
  matchesHandle: StateHandle<Annotation[]>;
}> = (props) => {
  const { message } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);
  const [editHeight, setEditHeight] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [matches, _setMatches] = props.matchesHandle;

  const formatEntries = Object.entries(message.format);

  const messageContentFormatted = useMemo(() => {
    let prettyJson: string;

    try {
      // This was observed to fail in prod, seems to indicate backend response is invalid or tampered with
      prettyJson = JSON.stringify(JSON.parse(message.content ?? "{}"), null, 2);
    } catch (err) {
      prettyJson = message.content || "";
    }

    return message.format.__typename === "FunctionSystemPrompt" ||
      message.format.__typename === "StructuredResponseSchema" ||
      message.format.__typename === "ProfileResponse"
      ? prettyJson
      : message.content;
  }, [message.format, message.content]);

  return (
    <Card sx={{ mb: 1, display: "flex", flexDirection: "column", minWidth: "1200px" }}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}>
        <Box
          sx={{
            position: "relative",
            minWidth: 1,
            width: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}>
          {isEditing ? (
            <Box>
              <textarea
                defaultValue={getTextContentForMessage(message)}
                style={{
                  padding: 0,
                  border: "none",
                  width: "100%",
                  height: editHeight,
                  fontFamily: "monospace",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                }}>
                <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (props.updateMessage) {
                      const newMessage = {
                        messageIndex: message.index,
                        content: (document.querySelector("textarea") as HTMLTextAreaElement).value,
                      };
                      props.updateMessage(newMessage);
                    }
                    setIsEditing(false);
                  }}>
                  Update
                </Button>
              </Box>
            </Box>
          ) : (
            <Box ref={messageRef}>
              <p className="text-md">{message.format.__typename}</p>
              <div className="flex flex-row gap-2">
                {formatEntries &&
                  formatEntries.map((entry) => (
                    <p className="text-xxs" key={entry[0]}>
                      {entry[0]}: {entry[1].toString()}
                    </p>
                  ))}
              </div>
              {message.format.__typename === "ProfileResponse" ? (
                <ProfileResponseTable content={message.content ?? "{}"} updateMatches={props.updateMatches} />
              ) : null}
              {message.format.__typename !== "ProfileResponse" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "auto",
                  }}>
                  {message.format.__typename === "SystemPrompt" && (
                    <p className="text-xs whitespace-pre max-h-[200px]">{messageContentFormatted}</p>
                  )}
                  {message.format.__typename === "StructuredResponseSchema" && (
                    <P
                      variant="sm"
                      sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", maxHeight: "200px", fontSize: "10px" }}>
                      {messageContentFormatted}
                    </P>
                  )}
                  {message.format.__typename === "FunctionSystemPrompt" && (
                    <P
                      variant="sm"
                      sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", maxHeight: "200px", fontSize: "10px" }}>
                      {messageContentFormatted}
                    </P>
                  )}
                  {message.format.__typename === "Blocks" && (
                    <RainbowText
                      sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", fontSize: "10px" }}
                      annotations={matches}
                      text={message.content ?? ""}
                    />
                  )}
                  {message.format.__typename === "PageImage" &&
                    (message.contentParts ?? []).map((part, idx) => {
                      return (
                        <Card key={idx} sx={{ p: 1, m: 1 }}>
                          <P>{part.contentType} part</P>
                          {part.contentType === "text" && (
                            <RainbowText
                              sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                              text={part.textContent ?? ""}
                              annotations={matches}
                            />
                          )}
                        </Card>
                      );
                    })}
                  {message.format.__typename === "ManualMessage" && (
                    <P sx={{ whiteSpace: "pre-wrap" }} variant="sm">
                      {message.content}
                    </P>
                  )}
                  {message.format.__typename === "AgentToolCall" && (
                    <p className="text-xs whitespace-pre max-h-[200px]">
                      {JSON.stringify(message.format.toolCalls, undefined, 2)}
                    </p>
                  )}
                  {message.format.__typename === "AgentTool" && (
                    <p className="text-xs whitespace-pre max-h-[200px]">
                      {message.contentParts?.map((contentPart) => {
                        try {
                          return JSON.stringify(JSON.parse(contentPart.textContent || ""), undefined, 2);
                        } catch {
                          return contentPart.textContent;
                        }
                      })}
                    </p>
                  )}
                  {message.format.__typename === "AgentOutput" && (
                    <P sx={{ whiteSpace: "pre-wrap" }} variant="sm">
                      {message.contentParts?.map((contentPart) => contentPart.textContent)}
                    </P>
                  )}
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={() => setIsExpanded(true)}>
          Expand
        </Button>
        {props.editable && (
          <Box>
            <Tooltip title="Edit" arrow>
              <IconButton
                onClick={() => {
                  if (isEditing) {
                    setIsEditing(false);
                  } else {
                    setIsEditing(true);
                    setEditHeight(messageRef.current?.clientHeight || 0);
                  }
                }}>
                <Edit />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardActions>
      <Dialog
        open={isExpanded}
        maxWidth="xl"
        onClose={() => {
          setIsExpanded(false);
        }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <P variant="lg">Message Content</P>
          <CopyToClipboard title="Copy Text" text={message.content ?? JSON.stringify(message.contentParts)} />
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              borderRadius: 0.5,
              backgroundColor: theme.palette.grey[50],
              overflow: "auto",
            }}>
            <P
              sx={{
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
              }}
              variant="sm">
              {messageContentFormatted}
            </P>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsExpanded(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AiChatMessageComponent;
